/**
 * Plochy webu, na kterých se fotka ořezává (vize podnety-tymu-2026-10, F8.4): náhledy
 * v detailu fotky v adminu je kreslí v tomto pořadí a skript snímků zpětného ohniska z nich
 * počítá výřez. Výřez náhledu v adminu kreslí prohlížeč (`object-fit: cover`
 * s `object-position`) stejně jako web; `visibleRegion` je tatáž geometrie v číslech.
 */

export const FOCAL_SURFACE_IDS = [
  "heroMobile",
  "heroDesktop",
  "card16x10",
  "card3x4",
  "square",
] as const;
export type FocalSurfaceId = (typeof FOCAL_SURFACE_IDS)[number];

/** Poměr plochy šířka : výška; pořadí klíčů = pořadí náhledů v adminu. */
export const FOCAL_SURFACES: Readonly<Record<FocalSurfaceId, { w: number; h: number }>> = {
  heroMobile: { w: 390, h: 844 },
  heroDesktop: { w: 1440, h: 900 },
  card16x10: { w: 16, h: 10 },
  card3x4: { w: 3, h: 4 },
  square: { w: 1, h: 1 },
};

/** Výřez jedné osy: obraz délky `img` zvětšený měřítkem `scale` v rámečku délky `box`. */
function axis(img: number, box: number, scale: number, pos: number): [number, number] {
  const rendered = img * scale;
  const visible = Math.min(1, box / rendered);
  const start = (1 - visible) * Math.min(1, Math.max(0, pos));
  return [start, start + visible];
}

/**
 * Oblast obrazu `imgW`×`imgH` viditelná v rámečku `boxW`×`boxH` při `object-fit: cover`
 * a `object-position` `x`, `y` (0..1, tj. 0 % až 100 %). Výsledek ve zlomcích 0..1 obrazu:
 * `x0`, `y0` levý horní roh, `x1`, `y1` pravý dolní. Rámeček stačí zadat poměrem stran.
 *
 * `object-position: p` posune obraz tak, že bod `p` obrazu leží v bodě `p` rámečku, takže
 * začátek výřezu je `(1 − viditelný podíl) · p`.
 */
export function visibleRegion(
  imgW: number,
  imgH: number,
  boxW: number,
  boxH: number,
  x: number,
  y: number,
): { x0: number; y0: number; x1: number; y1: number } {
  if (!(imgW > 0 && imgH > 0 && boxW > 0 && boxH > 0)) {
    throw new RangeError("visibleRegion: rozměry obrazu i rámečku musí být kladné");
  }
  const scale = Math.max(boxW / imgW, boxH / imgH);
  const [x0, x1] = axis(imgW, boxW, scale, x);
  const [y0, y1] = axis(imgH, boxH, scale, y);
  return { x0, y0, x1, y1 };
}
